import { todaySaoPaulo } from "../../utils/format";
import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "../../api/client";
import { saveBlobResponse } from "../../utils/download";
import Header from "../../components/layout/Header";
import { Button } from "../../components/ui/button";
import WizardStepper from "../../components/common/WizardStepper";
import Step1Client from "./steps/Step1Client";
import Step2Services from "./steps/Step2Services";
import Step3Pricing from "./steps/Step3Pricing";
import Step4Review from "./steps/Step4Review";
import { toast } from "sonner";
import { AlertTriangle, ChevronLeft, ChevronRight, Save, Loader2, CheckCircle2, LockKeyhole, MapPin, Camera, ClipboardPlus } from "lucide-react";
import MasterKeyModal from "../../components/common/MasterKeyModal";
import { useAuth } from "../../context/AuthContext";

const initialData = {
  client_id: "",
  data: todaySaoPaulo(),
  validade_dias: 15,
  duracao_meses: 12,
  num_parcelas: 12,
  desconto_pct: 0,
  programas_laudos: [],
  acompanhamento: {
    num_funcionarios: 0,
    valor_por_funcionario: 0,
    visitas_previstas: 0,
    valor_por_visita: 0,
    custo_visita: 0,
    custo_acompanhamento_funcionario: 0,
    exibir_num_funcionarios: false,
  },
  avaliacoes_fixas: [],
  avaliacoes_quimicas: [],
  treinamentos: [],
  escopo_descricao: "",
  preproposal_sources: [],
  ajuste_preco_pct: 0,
  cidade_referencia: "",
  inclusos: [],
  nao_inclusos: [],
  prazos: [],
  condicoes_comerciais: [],
  nao_inclusos_extra: [],
  observacoes: "",
  exibir_precos_itens: false,
  status: "rascunho",
};

const STEP_DESCRIPTIONS = {
  Cliente: "Selecione o cliente e confirme os dados-base da oportunidade.",
  Serviços: "Monte o escopo técnico e comercial que fará parte da proposta.",
  Precificação: "Revise custos, margens, condições e composição de preço.",
  Revisão: "Confira a apresentação final antes de salvar ou gerar o documento.",
};

export default function NewProposal() {
  const { id } = useParams();
  const isEdit = id && id !== "nova";
  const navigate = useNavigate();
  const { isAdmin, isCollaborator } = useAuth();
  const STEPS_VISIBLE = isAdmin
    ? [{ label: "Cliente" }, { label: "Serviços" }, { label: "Precificação" }, { label: "Revisão" }]
    : [{ label: "Cliente" }, { label: "Serviços" }, { label: "Revisão" }];

  const [step, setStep] = useState(0);
  const [data, setData] = useState(initialData);
  const [calc, setCalc] = useState(null);
  const [loading, setLoading] = useState(false);
  const [propId, setPropId] = useState(isEdit ? id : null);
  const [showMaster, setShowMaster] = useState(false);
  const [autoSavedAt, setAutoSavedAt] = useState(null);
  const [autoSaving, setAutoSaving] = useState(false);
  const [preVisitLoading, setPreVisitLoading] = useState(false);
  const [importingVisit, setImportingVisit] = useState("");
  const [proposalVisits, setProposalVisits] = useState([]);

  const isSuperseded = Boolean(data?.superseded_by_id);
  const isAccepted = data?.status === "aceita";
  const locked = Boolean(isEdit && (isSuperseded || isAccepted));
  const lockedReason = isSuperseded
    ? `Esta versão foi substituída${data?.superseded_by_code ? ` por ${data.superseded_by_code}` : " por uma revisão posterior"}. O conteúdo histórico é somente leitura.`
    : isAccepted
      ? "Esta proposta já foi aceita. O conteúdo comercial é imutável; crie uma revisão pelo Pipeline Comercial para negociar alterações."
      : "";

  useEffect(() => {
    if (!isEdit) return;
    api.get(`/proposals/${id}`).then((r) => {
      const incoming = r.data || {};
      setData({
        ...initialData,
        ...incoming,
        acompanhamento: { ...initialData.acompanhamento, ...(incoming.acompanhamento || {}) },
        preproposal_sources: incoming.preproposal_sources || [],
      });
      setCalc(incoming.calc);
    }).catch(() => toast.error("Falha ao carregar proposta"));
  }, [id, isEdit]);

  useEffect(() => {
    if (!propId) { setProposalVisits([]); return; }
    api.get(`/field-visits/proposal/${propId}`)
      .then((r) => setProposalVisits(r.data || []))
      .catch(() => setProposalVisits([]));
  }, [propId]);

  const calcSeqRef = useRef(0);
  const recalc = useCallback(async (d) => {
    if (!d.client_id) return;
    const seq = ++calcSeqRef.current;
    try {
      const r = await api.post("/proposals/calculate", d);
      if (seq === calcSeqRef.current) setCalc(r.data);
    } catch (e) {
      console.error("[proposal] recalc failed:", e?.response?.data?.detail || e?.message || e);
    }
  }, []);

  useEffect(() => {
    if (locked) return;
    const t = setTimeout(() => recalc(data), 300);
    return () => clearTimeout(t);
  }, [data, recalc, locked]);

  const propIdRef = useRef(propId);
  useEffect(() => { propIdRef.current = propId; }, [propId]);
  const savingRef = useRef(false);
  const pendingSaveRef = useRef(null);
  const saveWaitersRef = useRef([]);

  const persist = useCallback(async (d, { silent = false } = {}) => {
    if (!d.client_id) return null;
    if (d?.superseded_by_id || d?.status === "aceita") {
      if (!silent) toast.error("Esta versão é somente leitura. Crie uma revisão comercial para alterar o conteúdo.");
      return null;
    }

    pendingSaveRef.current = { data: d, silent };
    if (savingRef.current) {
      return new Promise((resolve) => saveWaitersRef.current.push(resolve));
    }

    savingRef.current = true;
    setAutoSaving(true);
    let lastResult = null;

    try {
      while (pendingSaveRef.current) {
        const job = pendingSaveRef.current;
        pendingSaveRef.current = null;
        try {
          let r;
          if (propIdRef.current) {
            r = await api.put(`/proposals/${propIdRef.current}`, job.data);
          } else {
            r = await api.post("/proposals", job.data);
            propIdRef.current = r.data.id;
            setPropId(r.data.id);
          }
          lastResult = r.data;
          if (r?.data?.calc) setCalc(r.data.calc);
          setAutoSavedAt(new Date());
        } catch (e) {
          if (!job.silent) toast.error(e.response?.data?.detail || "Falha ao salvar");
          else console.error("[proposal] autosave failed:", e?.response?.data?.detail || e?.message || e);
        }
      }
    } finally {
      savingRef.current = false;
      setAutoSaving(false);
      const waiters = saveWaitersRef.current.splice(0);
      waiters.forEach((resolve) => resolve(lastResult));
    }
    return lastResult;
  }, []);

  useEffect(() => {
    if (!data.client_id || locked) return;
    const t = setTimeout(() => persist(data, { silent: true }), 800);
    return () => clearTimeout(t);
  }, [data, persist, locked]);

  const next = () => setStep((s) => Math.min(s + 1, STEPS_VISIBLE.length - 1));
  const prev = () => setStep((s) => Math.max(s - 1, 0));

  const saveDraft = async () => {
    if (locked) return toast.error("Esta versão é somente leitura. Crie uma revisão pelo Pipeline Comercial.");
    if (!data.client_id) return toast.error("Selecione um cliente primeiro");
    setLoading(true);
    try {
      const saved = await persist(data, { silent: false });
      if (saved) toast.success("Proposta salva como rascunho");
      return saved;
    } finally { setLoading(false); }
  };

  const openPreProposalVisit = async () => {
    if (locked) return toast.error("Esta versão é somente leitura. Crie uma revisão antes de realizar novo levantamento.");
    if (!data.client_id) return toast.error("Selecione um cliente antes de abrir a visita pré-proposta");
    setPreVisitLoading(true);
    try {
      const saved = await persist(data, { silent: false });
      const proposalId = saved?.id || propIdRef.current;
      if (!proposalId) throw new Error("Não foi possível salvar o rascunho da proposta");
      const r = await api.post("/field-visits/pre-proposal", {
        proposal_id: proposalId,
        client_id: data.client_id,
        objetivo: "Levantamento técnico em campo para definição do escopo, condições e premissas desta proposta.",
      });
      toast.success(r.data.reused ? "Visita pré-proposta em aberto localizada" : "Visita pré-proposta criada");
      navigate(`/operacoes/visitas/${r.data.visit.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Falha ao abrir visita pré-proposta");
    } finally { setPreVisitLoading(false); }
  };

  const importVisitIntoScope = async (visit) => {
    if (locked) return toast.error("Esta versão é somente leitura.");
    if ((data.preproposal_sources || []).includes(visit.id)) return toast.info("Este levantamento já faz parte do escopo desta proposta");
    if (visit.status !== "concluida") return toast.error("Conclua o levantamento antes de importá-lo para o escopo");
    setImportingVisit(visit.id);
    try {
      const r = await api.get(`/field-visits/${visit.id}/scope-source`);
      const block = String(r.data?.scope_text || "").trim();
      if (!block) throw new Error("O levantamento não possui texto utilizável");
      setData((current) => ({
        ...current,
        escopo_descricao: [String(current.escopo_descricao || "").trim(), block].filter(Boolean).join("\n\n"),
        preproposal_sources: Array.from(new Set([...(current.preproposal_sources || []), visit.id])),
      }));
      setStep(1);
      toast.success(`${r.data.findings_count || 0} registro(s) copiado(s) literalmente para o escopo. Revise antes de gerar a proposta.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Falha ao importar levantamento para o escopo");
    } finally { setImportingVisit(""); }
  };

  const generate = async () => {
    if (locked) return toast.error("Não é possível regenerar uma versão histórica ou já aceita.");
    const saved = await saveDraft();
    if (!saved) return;
    setLoading(true);
    try {
      await api.post(`/proposals/${saved.id}/generate`);
      toast.success("Proposta gerada com sucesso!");
      const r = await api.get(`/proposals/${saved.id}/download`, { responseType: "blob" });
      const cliRaw = saved?.client_snapshot?.razao_social || "cliente";
      const cli = cliRaw.replace(/[\\/:*?"<>|]/g, "").trim() || "cliente";
      const codigo = saved?.codigo_base && saved?.revision_number != null
        ? `${saved.codigo_base}-R${String(saved.revision_number).padStart(2, "0")}`
        : saved?.codigo || (saved?.numero != null ? String(saved.numero) : "");
      const base = codigo ? `Proposta ${codigo} - ${cli}` : `Proposta - ${cli}`;
      saveBlobResponse(r, base);
      navigate("/propostas");
    } catch (e) { toast.error(e.response?.data?.detail || "Falha ao gerar"); }
    finally { setLoading(false); }
  };

  const currentStep = STEPS_VISIBLE[step]?.label || "Proposta";

  return (
    <>
      <Header
        title={locked ? "Visualizar Proposta" : isEdit ? "Editar Proposta" : "Nova Proposta"}
        subtitle={locked ? "Versão preservada para rastreabilidade comercial" : "Configure o escopo, a apresentação comercial e gere a proposta"}
        actions={<div className="flex flex-wrap gap-2">{!locked && <Button variant="outline" onClick={openPreProposalVisit} disabled={preVisitLoading || !data.client_id} data-testid="pre-proposal-visit-btn">{preVisitLoading ? <Loader2 className="size-4 animate-spin" /> : <MapPin className="size-4" />} Visita pré-proposta</Button>}<Button variant="outline" onClick={() => navigate("/propostas")} data-testid="cancel-wizard-btn"><ChevronLeft className="size-4 mr-1" /> Voltar</Button></div>}
      />

      <div className="mx-auto w-full max-w-[1500px] space-y-5 p-4 sm:p-6 lg:p-8" data-testid="new-proposal-page">
        {locked && (
          <div className="flex items-start gap-3 rounded-2xl border border-amber-500/30 bg-amber-500/10 px-4 py-3.5 text-sm text-amber-800 shadow-sm dark:text-amber-200">
            {isSuperseded ? <AlertTriangle className="mt-0.5 size-5 shrink-0" /> : <LockKeyhole className="mt-0.5 size-5 shrink-0" />}
            <div>
              <div className="font-semibold">Versão somente leitura</div>
              <div className="mt-0.5 leading-relaxed opacity-80">{lockedReason}</div>
            </div>
          </div>
        )}

        {step === 0 && proposalVisits.length > 0 && (
          <div className="rounded-2xl border border-primary/20 bg-primary/[0.04] p-4 sm:p-5" data-testid="proposal-field-visits">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <div className="label-tech">Levantamento em campo</div>
                <h3 className="mt-1 font-semibold">Visitas vinculadas a esta proposta</h3>
                <p className="mt-1 text-sm text-muted-foreground">Fotos e observações permanecem ligadas ao rascunho. Somente levantamentos concluídos podem ser copiados para o escopo, sempre com texto literal.</p>
              </div>
              {!locked && <Button size="sm" variant="outline" onClick={openPreProposalVisit}><MapPin className="size-4" /> Abrir levantamento</Button>}
            </div>
            <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
              {proposalVisits.map((visit) => {
                const imported = (data.preproposal_sources || []).includes(visit.id);
                return (
                  <div key={visit.id} className="rounded-xl border border-border/70 bg-card p-4">
                    <div className="flex items-center justify-between gap-3"><span className="text-sm font-medium">{visit.titulo || "Visita pré-proposta"}</span><MapPin className="size-4 text-primary" /></div>
                    <div className="mt-2 flex flex-wrap gap-3 text-xs text-muted-foreground"><span>{visit.status}</span><span>{visit.findings_count || 0} registro(s)</span><span className="inline-flex items-center gap-1"><Camera className="size-3" /> {visit.photos_count || 0}</span></div>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <Button type="button" size="sm" variant="outline" onClick={() => navigate(`/operacoes/visitas/${visit.id}`)}><MapPin className="size-3.5" /> Abrir</Button>
                      {!locked && <Button type="button" size="sm" variant={imported ? "secondary" : "outline"} disabled={imported || visit.status !== "concluida" || importingVisit === visit.id} onClick={() => importVisitIntoScope(visit)}>{importingVisit === visit.id ? <Loader2 className="size-3.5 animate-spin" /> : imported ? <CheckCircle2 className="size-3.5" /> : <ClipboardPlus className="size-3.5" />} {imported ? "No escopo" : "Usar no escopo"}</Button>}
                    </div>
                    {visit.status !== "concluida" && !imported && <div className="mt-2 text-[11px] text-muted-foreground">Conclua a visita para liberar a importação.</div>}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <WizardStepper steps={STEPS_VISIBLE} current={step} onJump={(i) => i <= step && setStep(i)} />

        <div className="overflow-hidden rounded-2xl border border-border/80 bg-card shadow-sm">
          <div className="flex flex-col gap-3 border-b border-border/70 bg-muted/20 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
            <div>
              <div className="label-tech">Etapa {step + 1} de {STEPS_VISIBLE.length}</div>
              <h2 className="mt-1 text-lg font-semibold tracking-tight">{currentStep}</h2>
              <p className="mt-1 max-w-3xl text-sm text-muted-foreground">{STEP_DESCRIPTIONS[currentStep]}</p>
            </div>
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              {locked ? (
                <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-background px-3 py-1.5">
                  <LockKeyhole className="size-3.5" /> Histórico preservado
                </span>
              ) : autoSaving ? (
                <span className="inline-flex items-center gap-1.5 rounded-full border border-primary/20 bg-primary/5 px-3 py-1.5 text-primary" data-testid="autosave-saving">
                  <Loader2 className="size-3.5 animate-spin" /> Salvando alterações…
                </span>
              ) : autoSavedAt ? (
                <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-500/20 bg-emerald-500/10 px-3 py-1.5 text-emerald-700 dark:text-emerald-300" data-testid="autosave-indicator">
                  <CheckCircle2 className="size-3.5" /> Salvo às {autoSavedAt.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
                </span>
              ) : (
                <span className="rounded-full border border-border bg-background px-3 py-1.5">Autosave ativo</span>
              )}
            </div>
          </div>

          <fieldset disabled={locked} className={locked ? "opacity-[0.84]" : ""}>
            <div className="min-h-[420px] p-4 sm:p-6 lg:p-7">
              {step === 0 && <Step1Client data={data} setData={setData} onMasterRequest={() => setShowMaster(true)} readOnly={locked || isCollaborator} />}
              {step === 1 && <Step2Services data={data} setData={setData} onMasterRequest={() => setShowMaster(true)} readOnly={locked || isCollaborator} />}
              {isAdmin && step === 2 && <Step3Pricing data={data} setData={setData} calc={calc} />}
              {((isAdmin && step === 3) || (isCollaborator && step === 2)) && <Step4Review data={data} setData={setData} calc={calc} hideFinancial={isCollaborator} />}
            </div>
          </fieldset>
        </div>

        <div className="sticky bottom-3 z-20 rounded-2xl border border-border/80 bg-background/95 p-3 shadow-lg backdrop-blur sm:p-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-2">
              {step > 0 && (
                <Button variant="outline" onClick={prev} data-testid="wizard-prev-btn" className="w-full sm:w-auto">
                  <ChevronLeft className="size-4 mr-1" /> Anterior
                </Button>
              )}
            </div>

            <div className="grid grid-cols-1 gap-2 sm:flex sm:items-center">
              {!locked && (
                <Button variant="outline" onClick={saveDraft} disabled={loading} data-testid="wizard-save-draft-btn">
                  <Save className="size-4 mr-1" /> Salvar rascunho
                </Button>
              )}

              {step < STEPS_VISIBLE.length - 1 ? (
                <Button onClick={next} data-testid="wizard-next-btn">
                  Próximo <ChevronRight className="size-4 ml-1" />
                </Button>
              ) : !locked ? (
                <Button onClick={generate} disabled={loading || autoSaving} data-testid="wizard-generate-btn" size="lg">
                  {loading ? "Gerando..." : "Gerar Proposta PDF"}
                </Button>
              ) : null}
            </div>
          </div>
        </div>
      </div>

      <MasterKeyModal open={showMaster} onOpenChange={setShowMaster} reason="Para carregar custos cadastrados (criptografados), desbloqueie a senha mestra." />
    </>
  );
}

